import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";

interface NavChild {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "입학안내",
    to: "/admission",
    children: [
      { label: "입학절차", to: "/admission#process" },
      { label: "입학테스트", to: "/admission#test" },
    ],
  },
  {
    label: "수업안내",
    to: "/classes",
    children: [
      { label: "초등부", to: "/classes#elementary" },
      { label: "중등부", to: "/classes#middle" },
      { label: "고등부", to: "/classes#high" },
    ],
  },
  {
    label: "시간표",
    to: "/schedule",
    children: [
      { label: "학기중", to: "/schedule#term" },
      { label: "방학중", to: "/schedule#vacation" },
    ],
  },
  {
    label: "학원안내",
    to: "/about",
    children: [
      { label: "학원장 인사말", to: "/about#greeting" },
      { label: "강사진 소개", to: "/about#teachers" },
    ],
  },
];

const PHONE = "02-002-2525";
const PHONE_HREF = "tel:020022525";

export default function Header() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          solid
            ? "border-b border-white/10 bg-foreground-950/95 backdrop-blur-md"
            : "bg-gradient-to-b from-foreground-950/80 via-foreground-950/25 to-transparent"
        }`}
      >
        <div className="mx-auto max-w-content px-5 md:px-8">
          <div className="flex h-16 items-center justify-between md:h-20">
            <Logo light />

            <nav className="hidden items-center gap-1 lg:flex">
              {NAV_ITEMS.map((item) => {
                const active = location.pathname.startsWith(item.to);
                return (
                  <div key={item.to} className="group relative">
                    <Link
                      to={item.to}
                      className={`relative flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium tracking-tight transition-colors cursor-pointer ${
                        active ? "text-primary-500" : "text-white/80 hover:text-primary-500"
                      }`}
                    >
                      {item.label}
                      <i className="ri-arrow-down-s-line text-xs opacity-60" />
                      <span
                        className={`absolute inset-x-4 -bottom-0.5 h-0.5 origin-left bg-primary-500 transition-transform duration-300 ${
                          active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                        }`}
                      />
                    </Link>
                    {item.children && (
                      <div className="invisible absolute left-0 top-full -translate-y-1 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="min-w-[190px] rounded-2xl border border-white/10 bg-foreground-950 p-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              className="block rounded-xl px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-primary-500 hover:text-foreground-950 cursor-pointer"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 md:gap-4">
              <a
                href={PHONE_HREF}
                className="hidden items-center gap-2 text-white/80 transition-colors hover:text-primary-500 xl:flex cursor-pointer"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20">
                  <i className="ri-phone-line text-sm" />
                </span>
                <span className="font-label text-sm font-medium tracking-wide">{PHONE}</span>
              </a>

              <Link
                to="/consult"
                className="group relative inline-flex items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-bold tracking-tight text-foreground-950 transition-colors hover:bg-primary-400 md:px-5 cursor-pointer"
              >
                <span className="shine absolute inset-y-0 left-0 w-1/2 bg-white/50 blur-md" />
                <span className="relative z-10">상담신청</span>
                <i className="ri-arrow-right-line relative z-10" />
              </Link>

              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="전체 메뉴 열기"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 lg:hidden cursor-pointer"
              >
                <i className="ri-menu-line text-xl" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          open ? "visible opacity-100" : "invisible opacity-0"
        } transition-all duration-300`}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-foreground-950" />
        <div className="absolute inset-0 grain-light opacity-40" />
        <div className="relative flex h-full flex-col overflow-y-auto">
          <div className="flex h-16 items-center justify-between px-5">
            <Logo light />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="메뉴 닫기"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>

          <nav className="flex-1 px-5 pb-4 pt-4">
            {NAV_ITEMS.map((item, idx) => (
              <div
                key={item.to}
                style={{ transitionDelay: open ? `${idx * 60 + 80}ms` : "0ms" }}
                className={`border-b border-white/10 py-5 transition-all duration-500 ${
                  open ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
              >
                <Link to={item.to} className="flex items-center justify-between cursor-pointer">
                  <span className="font-heading text-2xl font-black text-white">{item.label}</span>
                  <i className="ri-arrow-right-up-line text-xl text-primary-500" />
                </Link>
                {item.children && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/70 cursor-pointer"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="space-y-3 border-t border-white/10 px-5 py-6">
            <a
              href={PHONE_HREF}
              className="flex items-center gap-2 text-white/70 cursor-pointer"
            >
              <i className="ri-phone-line text-primary-500" />
              <span className="font-label text-sm tracking-wide">{PHONE}</span>
            </a>
            <Link
              to="/consult"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 py-4 font-bold text-foreground-950 cursor-pointer"
            >
              상담신청
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}